import React, { Fragment } from 'react';
import clsx from 'classnames';
import CaretDown from '@components/Icons/CaretDown';
import SubscriptionCancelDate from '@components/SubscriptionCancelDate';
import * as graphQLModels from '@typings/graphql-models';
import { SubscriptionStatus } from '@typings/graphql-models';
import { m } from 'framer-motion';
import * as format from '@format';

import { getDateFormat } from '@helpers/helper';
import s from './SubscriptionListItem.module.scss';

type Subscription = Pick<graphQLModels.PersonSubscription, 'subscriptionStatus' | 'canceledAt' | 'createdAt' | 'lastBillingDate'> & {
  discountedPrice: number;
  priceConnection?: Pick<graphQLModels.Price, 'price' | 'currency'> | null;
  subscriptionModel?: {
    product?:
      | (Pick<graphQLModels.Product, 'title' | 'slug'> & {
          image?: { url: string } | null;
          prices: Array<Pick<graphQLModels.Price, 'price' | 'recurringInterval' | 'currency'>>;
        })
      | null;
  } | null;
};

type Props<S> = {
  item: S;
  isOpen?: boolean;
  setActiveSubscription: (item: S | null) => void;
};

const AnimatedCaret = m(CaretDown);

const SubscriptionListItem = <S extends Subscription>({ item, setActiveSubscription, isOpen }: Props<S>): React.ReactElement => {
  const { lastBillingDate, priceConnection, subscriptionStatus, createdAt } = item;
  const { title, prices } = item?.subscriptionModel?.product || {};
  const activePrice = prices?.find(p => p.price === priceConnection?.price);
  return (
    <button className={s['subscription-item']} onClick={() => setActiveSubscription(item)}>
      <div className={s['subscription-item__content']}>
        <strong>{title}</strong>
        <div className={s['subscription-item__status']}>
          {!item.canceledAt && (
            <span>
              <span className={clsx(s['subscription-item__status'], s[`subscription-item__status--${subscriptionStatus}`])}>
                {subscriptionStatus === SubscriptionStatus.PastDue && `Your payment is past due${lastBillingDate ? ` since ${getDateFormat(lastBillingDate)}` : ''}`}
                {subscriptionStatus !== SubscriptionStatus.PastDue && `${subscriptionStatus} starting ${getDateFormat(createdAt)}`}
              </span>
            </span>
          )}
          {activePrice && !item.canceledAt && subscriptionStatus !== graphQLModels.SubscriptionStatus.Canceled
            ? (
              <Fragment>
                <span className={s['subscription-item__separator']}>·</span>
                <span className={s['subscription-item__price']}>
                  {format.price.recurring(activePrice, item.discountedPrice)}
                </span>
              </Fragment>
            )
            : null}
          {item.canceledAt && (
            <>
              <span className={s['subscription-item__separator']}>·</span>
              <SubscriptionCancelDate cancelDate={item.canceledAt} />
            </>
          )}
        </div>
      </div>
      <AnimatedCaret animate={{ rotate: isOpen ? -180 : 0 }} size={12} />
    </button>
  );
};

export default SubscriptionListItem;
